import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cN from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@Redux/search/store';
import { I_RootState } from '@Redux/search/interfaces';
import { sendEmptyDataLayer } from 'src/hooks/useDataLayer';

import {
    setFilterDataLoading,
    getData,
    setSendEmitApi,
    setCityLength,
} from '@Redux/search/actions';
import ResultCards from '@components/SearchPage/ResultCards';
import Recommends from '@components/SearchPage/Recommends';
import ResultNoResult from '@components/SearchPage/ResultNoData';
import Sorters from '@components/SearchPage/Sorters';
import Filters from '@components/SearchPage/Filters';
import { selectUrl } from '@utils/utils';
import styles from './styles.module.scss';
const switchByOptions = [
    {
        text: '卡牌',
        value: 'Cards',
    },
    {
        text: '列表',
        value: 'List',
    },
];
// 因filter 跟sort屬於不同元件，但手機的篩選slide會有state影響，故把scroll fixed事件放在index,以免此處state影響到scroll的狀態
const KEYWORD = window.sessionStorage.getItem('Keyword') || '';
const TOURBU = window.sessionStorage.getItem('TourBu') || '';
const FiltersWithSort: React.FC<{ isSticky?: string }> = ({ isSticky }) => {
    const [isFilterMOpen, setIsFilterMOpen] = useState<boolean>(false);
    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });
    const { TagList, FilterDataLoading, sendEmitApi, RecommendsData } = useSelector(
        (state: RootState): I_RootState => state.search.reducer
    );

    const { GroupFilterStore, GroupFilterSelected } = useSelector(
        (state: RootState) => state.search.GroupReducer
    );
    const { HotelFilterStore, HotelFilterSelected } = useSelector(
        (state: RootState) => state.search.HotelReducer
    );
    const { TicketFilterStore, TicketFilterSelected } = useSelector(
        (state: RootState) => state.search.TicketReducer
    );
    const { VocationFilterStore, VocationFilterSelected } = useSelector(
        (state: RootState) => state.search.VocationReducer
    );
    const { FlightFilterStore, FlightFilterSelected } = useSelector(
        (state: RootState) => state.search.FlightReducer
    );
    const { AllFilterStore, AllFilterSelected } = useSelector(
        (state: RootState) => state.search.AllReducer
    );
    const dispatch = useDispatch();

    const moduleClass = 'search';
    let filterStore = AllFilterStore;
    switch (TagList) {
        case 'grp':
            filterStore = GroupFilterStore;
            break;
        case 'htl':
            filterStore = HotelFilterStore;
            break;
        case 'vac':
            filterStore = VocationFilterStore;
            break;
        case 'etk':
            filterStore = TicketFilterStore;
            break;
        case 'flt':
            filterStore = FlightFilterStore;
            break;
        default:
            filterStore = AllFilterStore;
    }

    useEffect(() => {
        // TagList 切換清除光箱程式的長度
        dispatch(setCityLength({}));
    }, [TagList]);

    useEffect(() => {
        if (!KEYWORD) return;
        // TagList 切換都直接打 api，showAllDestination 跟 M 版滑入版面要確定按鈕才送出，用 sendEmitApi 判斷
        if (sendEmitApi) {
            const [params, apiUrl] = tagListSwitch();
            dispatch(setFilterDataLoading(true));
            dispatch(getData({ apiUrl, params }));
        }
    }, [
        TagList,
        GroupFilterSelected,
        HotelFilterSelected,
        TicketFilterSelected,
        VocationFilterSelected,
        FlightFilterSelected,
        AllFilterSelected,
    ]);

    const tagListSwitch = useCallback(() => {
        //參數字元有上限由IT控制
        let params = '';
        let apiUrl = 'searchall';

        switch (TagList) {
            case 'grp':
                params = selectUrl(GroupFilterSelected);
                if (typeof TOURBU == 'string' && TOURBU.length > 0) {
                    params += `TourBu=${TOURBU}&`;
                }
                apiUrl = 'searchtravel';
                break;
            case 'htl':
                params = selectUrl(HotelFilterSelected);
                apiUrl = 'searchhotel';
                break;
            case 'vac':
                params = selectUrl(VocationFilterSelected);
                apiUrl = 'searchvacation';
                break;
            case 'etk':
                params = selectUrl(TicketFilterSelected);
                apiUrl = 'searchactivity';
                break;
            case 'flt':
                params = selectUrl(FlightFilterSelected);
                apiUrl = 'searchflight';
                break;
            default:
                params = selectUrl(AllFilterSelected);
                if (typeof TOURBU == 'string' && TOURBU.length > 0) {
                    params += `TourBu=${TOURBU}&`;
                }
                apiUrl = 'searchall';
        }
        params += 'Keyword=' + KEYWORD;
        return [params, apiUrl];
    }, [
        TagList,
        GroupFilterSelected,
        HotelFilterSelected,
        TicketFilterSelected,
        VocationFilterSelected,
        FlightFilterSelected,
        AllFilterSelected,
    ]);
    useEffect(() => {
        if (isFilterMOpen) {
            //光箱打開時則關閉發送api選項
            dispatch(setSendEmitApi(false));
        } else {
            dispatch(setSendEmitApi(true));
        }
    }, [isFilterMOpen]);

    //排序資料
    const sortByOptions = useMemo(
        () => ({
            flatBtn: [
                {
                    text: '所有',
                    value: 'grp,vac,htl,etk,flt',
                },
                {
                    text: '團體',
                    value: 'grp',
                },
                {
                    text: '訂房',
                    value: 'htl',
                },
                {
                    text: '自由行',
                    value: 'vac',
                },
                {
                    text: '票券',
                    value: 'etk',
                },
                {
                    text: '機票',
                    value: 'flt',
                }
            ],
            dropdowns: [
                // 根據頁籤去籤換排序內容,seo_params參考/pages/index 的註解
                {
                    options: [
                        {
                            text: '依關聯度',
                            value: '',
                            seo_params: 'a',
                        },
                        {
                            text: '依售價低到高',
                            value: 1,
                            seo_params: 'b',
                        },
                        {
                            text: '依售價高到低',
                            value: 2,
                            seo_params: 'c',
                        },
                    ],
                    label: 'grp,vac,htl,etk,flt',
                },
                {
                    options: [
                        {
                            text: '依關聯度',
                            value: '',
                            seo_params: 'a',
                        },
                        {
                            text: '依售價低到高',
                            value: 1,
                            seo_params: 'b',
                        },
                        {
                            text: '依出發日',
                            value: 3,
                            seo_params: 'd',
                        },
                        {
                            text: '依天數', // 低到高
                            value: 4,
                            seo_params: 'e',
                        },
                        {
                            text: '熱門商品', // 高到低
                            value: 8,
                            seo_params: 'f',
                        },
                    ],
                    label: 'grp',
                },
                {
                    options: [
                        {
                            text: '依關聯度',
                            value: '',
                            seo_params: 'a',
                        },
                        {
                            text: '依售價低到高',
                            value: 1,
                            seo_params: 'b',
                        },
                        {
                            text: '依售價高到低',
                            value: 2,
                            seo_params: 'c',
                        },
                        {
                            text: '熱門商品', // 高到低
                            value: 4,
                            seo_params: 'f',
                        },
                        {
                            text: '雄獅精選', // 高到低
                            value: 6,
                            seo_params: 'g',
                        },
                        {
                            text: '評分高優先', // 高到低
                            value: 8,
                            seo_params: 'h',
                        },
                    ],
                    label: 'htl',
                },
                {
                    options: [
                        {
                            text: '依關聯度',
                            value: '',
                            seo_params: 'a',
                        },
                        {
                            text: '依售價低到高',
                            value: 1,
                            seo_params: 'b',
                        },
                        {
                            text: '依售價高到低',
                            value: 2,
                            seo_params: 'c',
                        },
                        {
                            text: '熱門商品',
                            value: 4,
                            seo_params: 'f',
                        },
                        {
                            text: '最多人買',
                            value: 6,
                            seo_params: 'i',
                        },
                        {
                            text: '依天數', // 低到高
                            value: 7,
                            seo_params: 'e',
                        },
                    ],
                    label: 'vac',
                },
                {
                    options: [
                        {
                            text: '依關聯度',
                            value: '',
                            seo_params: 'a',
                        },
                        {
                            text: '依售價低到高',
                            value: 1,
                            seo_params: 'b',
                        },
                        {
                            text: '依售價高到低',
                            value: 2,
                            seo_params: 'c',
                        },
                        {
                            text: '熱門商品',
                            value: 4,
                            seo_params: 'f',
                        },
                    ],
                    label: 'etk',
                },
                {
                    options: [
                        {
                            text: '依關聯度',
                            value: '',
                            seo_params: 'a',
                        },
                        {
                            text: '依售價低到高',
                            value: 1,
                            seo_params: 'b',
                        },
                        {
                            text: '依售價高到低',
                            value: 2,
                            seo_params: 'c',
                        },
                        {
                            text: '熱門商品',
                            value: 4,
                            seo_params: 'f',
                        },
                    ],
                    label: 'flt',
                },
            ],
        }),
        []
    );
    return (
        <>
            <div className={styles[`${moduleClass}_filtersWrap`]}>
                {/* 要有keyword跟api回傳的最大值跟目的地陣列資料，才有進階篩選 */}
                {KEYWORD &&
                    (filterStore.PriceMax !== 1 || filterStore.DestinationList.length !== 0) && (
                        <Filters isSlideOpen={isFilterMOpen} setSlideOpen={setIsFilterMOpen} />
                    )}
            </div>
            <div style={{ width: '100%' }}>
                <section className={cN(styles[`${moduleClass}_conditions`])}>
                    <div
                        className={cN(
                            isSticky === 'filters' ? `MobileSticky ` + styles.boxShadow : '',
                            styles.pt_15
                        )}
                    >
                        <Sorters
                            onClickFlatBtnM={setIsFilterMOpen}
                            switchByOptions={switchByOptions}
                            sortByOptions={sortByOptions}
                            isClearShadow={isSticky === 'filters' || !isMobile}
                        />
                    </div>
                    {/* 無關鍵字則顯示查無資料 */}
                </section>
                {KEYWORD ? (
                    <div className={`${moduleClass}_result`}>
                        <ResultCards />
                        <Recommends Recommend={RecommendsData} />
                    </div>
                ) : (
                    <ResultNoResult />
                )}
            </div>
        </>
    );
};

export default FiltersWithSort;
